import { lazy, Suspense, useEffect, useState } from 'react';
import {
  IconAdjustments,
  IconBarcode,
  IconBook2,
  IconCash,
  IconHome,
  IconLifebuoy,
  IconRocket,
  IconTruck,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Loader, Modal, Stack, Text } from '@wapharma/design-system';
import { ADMIN_DATA_SOURCES } from '../constants/adminNavLabels';
import { useStaffRoles } from '../hooks/useStaffRoles';
import { openSetupWizard } from '../lib/setupShell';
import { openLaunchWizard } from '../lib/launchShell';
import { NotificationSoundAccountToggle } from './NotificationSoundAccountToggle';
import { StockAlertsSettingsSection } from './StockAlertsSettingsSection';
import styles from './SettingsOverlay.module.css';

const LazyFulfillmentSettingsPage = lazy(() =>
  import('../pages/FulfillmentSettingsPage').then((m) => ({ default: m.FulfillmentSettingsPage })),
);
const LazyExternalCatalogDataSourcePage = lazy(() =>
  import('../pages/ExternalCatalogDataSourcePage').then((m) => ({
    default: m.ExternalCatalogDataSourcePage,
  })),
);

export type SettingsSectionKey =
  | 'general'
  | 'setup'
  | 'journey'
  | 'shipping'
  | 'payment'
  | 'data-sources';

type Props = {
  opened: boolean;
  onClose: () => void;
  initialSection?: SettingsSectionKey;
  /** Opens the report-issue wizard (hosted by AdminShell) above this modal. */
  onReportIssue: () => void;
};

/**
 * Settings modal — centered dialog with a section rail on the left.
 * Sections are pack-gated; hosted pages keep their own PageHeader + actions.
 * `setup` hands off to the SetupWizard sheet; `journey` is stubbed until OnboardingDock is ported.
 */
export function SettingsOverlay({ opened, onClose, initialSection = 'general', onReportIssue }: Props) {
  const navigate = useNavigate();
  const {
    isSuperAdmin,
    canMaintainStorefront,
    canConfigureCommerce,
    canPromo,
    canWriteCatalog,
    hasAny,
  } = useStaffRoles();

  const sections: { key: SettingsSectionKey; label: string; icon: typeof IconTruck }[] = [
    { key: 'general', label: 'Préférences', icon: IconAdjustments },
    { key: 'setup', label: 'Configuration initiale', icon: IconRocket },
    { key: 'journey', label: 'Parcours métier', icon: IconBook2 },
    ...(canConfigureCommerce
      ? [
          { key: 'shipping' as const, label: 'Livraison', icon: IconTruck },
          { key: 'payment' as const, label: 'Paiement', icon: IconCash },
        ]
      : []),
    ...(isSuperAdmin || canWriteCatalog
      ? [{ key: 'data-sources' as const, label: ADMIN_DATA_SOURCES.label, icon: IconBarcode }]
      : []),
  ];

  const [section, setSection] = useState<SettingsSectionKey>(initialSection);

  useEffect(() => {
    if (!opened) return;
    if (initialSection === 'setup') {
      openSetupWizard();
      onClose();
      return;
    }
    setSection(initialSection);
  }, [opened, initialSection, onClose]);

  const showStockAlerts =
    isSuperAdmin || hasAny('INVENTORY', 'CATALOG', 'SALES', 'VIEWER', 'WEB');

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Paramètres"
      size="xl"
      className={styles.panel}
      bodyClassName={styles.body}
    >
      {opened ? (
        <div className={styles.layout}>
          <nav className={styles.rail} aria-label="Sections des paramètres">
            {sections.map((s) => {
              const Icon = s.icon;
              const active = section === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  className={active ? `${styles.railItem} ${styles.railItemActive}` : styles.railItem}
                  aria-current={active ? 'true' : undefined}
                  onClick={() => {
                    if (s.key === 'setup') {
                      openSetupWizard();
                      onClose();
                      return;
                    }
                    setSection(s.key);
                  }}
                >
                  <Icon size={16} stroke={1.7} aria-hidden />
                  <span>{s.label}</span>
                </button>
              );
            })}
            <div className={styles.railFooter}>
              {canMaintainStorefront ? (
                <>
                  <button
                    type="button"
                    className={styles.railItem}
                    onClick={() => {
                      onClose();
                      navigate('/settings/storefront-home');
                    }}
                  >
                    <IconHome size={16} stroke={1.7} aria-hidden />
                    <span>Accueil marketing</span>
                  </button>
                  <button
                    type="button"
                    className={styles.railItem}
                    onClick={() => {
                      onClose();
                      navigate('/settings/storefront-site');
                    }}
                  >
                    <IconHome size={16} stroke={1.7} aria-hidden />
                    <span>Site vitrine</span>
                  </button>
                </>
              ) : null}
              {canPromo ? (
                <button
                  type="button"
                  className={styles.railItem}
                  onClick={() => {
                    onClose();
                    navigate('/settings/discount-codes');
                  }}
                >
                  <IconCash size={16} stroke={1.7} aria-hidden />
                  <span>Codes promo</span>
                </button>
              ) : null}
              {isSuperAdmin || hasAny('SALES') ? (
                <button
                  type="button"
                  className={styles.railItem}
                  onClick={() => {
                    onClose();
                    openLaunchWizard();
                  }}
                >
                  <IconLifebuoy size={16} stroke={1.7} aria-hidden />
                  <span>Prêt lancement</span>
                </button>
              ) : null}
              <button type="button" className={styles.railItem} onClick={onReportIssue}>
                <IconLifebuoy size={16} stroke={1.7} aria-hidden />
                <span>Signaler un problème</span>
              </button>
            </div>
          </nav>
          <div className={styles.content}>
            <Suspense
              fallback={
                <div className={styles.loading}>
                  <Loader />
                </div>
              }
            >
              {section === 'general' ? (
                <div className={styles.generalPane}>
                  <Stack gap="lg" style={{ maxWidth: 480 }}>
                    <Stack gap="xs">
                      <Text size="sm" fw={600}>
                        Notifications
                      </Text>
                      <NotificationSoundAccountToggle />
                    </Stack>
                    {showStockAlerts ? <StockAlertsSettingsSection /> : null}
                  </Stack>
                </div>
              ) : section === 'setup' ? (
                <Stack gap="sm" style={{ maxWidth: 480 }}>
                  <Text size="sm" fw={600}>
                    Configuration initiale
                  </Text>
                  <Text size="sm" c="dimmed">
                    L’assistant de démarrage (marques, équipe, catalogue) sera branché ici. En attendant,
                    utilisez le catalogue, les utilisateurs et les paramètres livraison / paiement.
                  </Text>
                </Stack>
              ) : section === 'journey' ? (
                <Stack gap="sm" style={{ maxWidth: 480 }}>
                  <Text size="sm" fw={600}>
                    Parcours métier
                  </Text>
                  <Text size="sm" c="dimmed">
                    Le guide opérationnel (dock d’onboarding) sera disponible ici. Pour l’instant, l’accueil
                    « À traiter » regroupe les files urgentes.
                  </Text>
                </Stack>
              ) : section === 'shipping' || section === 'payment' ? (
                <LazyFulfillmentSettingsPage mode={section} />
              ) : (
                <LazyExternalCatalogDataSourcePage />
              )}
            </Suspense>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
